// Shared catalog/cart business logic behind both ProposalTool's
// useProductsCatalog and PurchaseLink's usePurchaseLinkProducts — cohort
// access rules (BC/MC/TL), alternate-plan hiding, fee reconciliation,
// add/update/quantity/remove handlers, and the website-downgrade prompt.
// Redux-agnostic like usePaymentPreview: every slice-specific value (the
// active option/location, its country/Recurly info, the raw catalog groups)
// comes in as a plain param, and cart mutations go out via onItemsChange
// instead of dispatching directly — each thin wrapper hook owns reading its
// own slice's selectors and writing the result back (updateOption vs
// updateLocation), so this hook never needs to know which slice — or even
// which field (displayOrder vs locationId) — identifies the active item.
//
// manualCoupons/setManualCoupons are the one exception: app-store/coupon is
// already a single slice shared by both flows today, so there's nothing to
// parameterize there.
import {useCallback, useEffect, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useUserFeatures} from '../../../context/user-features-context'
import {useUserRoles} from '../../../utils/use-user-roles'
import {useAuthClient} from '../../../utils/use-auth-client'
import {toast} from 'react-toastify'
import {resolveApplicableFees} from '../../../utils/feeUtils'
import {
  resolveItemsAfterAdd,
  resolveItemsAfterRemove,
  resolveItemsAfterUpdate,
  applyQuantityToItems,
  getRenderRules,
  getIsNewTLMCAccessible,
  filterGroupsByCountry,
  filterGroupsByAccountContext,
  isSocialContentGenGrandfathered,
  getSubPlanCode,
} from '../utils/productSelectionRules'
import {getPriceForCountry} from '../utils/getPriceForCountry'
import {
  isTgpFoundationCatalogEnabled as isTgpFoundationCatalogFlagOn,
  isThryvBasicCatalogEnabled as isThryvBasicCatalogFlagOn,
  isTgpFoundationDowngrade,
} from '../../../utils/tgp-catalog-flags'

import {getLockedBillingByGroup} from '../utils/productPreloadRules'
import {
  WEBSITE_ADDON_CODE,
  WEBSITE_ENTITLEMENT_CODE,
  WEBSITE_DESIGN_FEE_PLAN,
} from '../utils/websiteDesignFee'
import {selectManualCoupons, setManualCoupons} from '../../../app-store/coupon'
import {
  MC_TRIAL_PLAN_CODES,
  FREE_ACCOUNT_CODES,
  KEAP_FAMILY_CODES,
  MC_2_0_SIGPREMIERPLANS,
  VALID_TLDS,
  GROWTH_PACKAGES,
  THRYV_STARTER_CODES,
} from '../../../utils/constants'

const COUNTRY_TO_CURRENCY = {
  US: 'USD',
  AU: 'AUD',
  CA: 'CAD',
  NZ: 'NZD',
  GB: 'GBP',
}

export function useCatalogCart({
  activeItem,
  country: selectedCountry,
  recurlyAccountInfo,
  rawAccountItems,
  rawSoftwareGroups,
  rawAddonGroups,
  isDataLoaded,
  oneTimeFeeDefinitions,
  productConfigInfo,
  clientType,
  onItemsChange,
}) {
  const dispatch = useDispatch()
  const authClient = useAuthClient()

  const [validExtensionList, setValidExtensionList] = useState(VALID_TLDS)
  useEffect(() => {
    authClient('keappartner/fetchConfigLookupValue/ValidUrlExtension')
      .then(response => {
        if (response?.data?.[0]?.value) {
          setValidExtensionList(response.data[0].value.split(','))
        }
      })
      .catch(() => {
        // Non-fatal: fall back to VALID_TLDS
      })
  }, [authClient])

  const userFeatures = useUserFeatures()
  const isKeapPartnerCatalog =
    productConfigInfo?.catalogSource === 'keap-partner'
  const isUserEnabledForMCFreeTrial = userFeatures?.MC_Free_Trial
  const isUserEnabledForMCAU = Boolean(userFeatures?.MC_AU)
  const isUserEnabledForMCNZ = Boolean(userFeatures?.MC_NZ)
  // GEP-7133 / FULSAS-13599: see "MC 5.0 -> TGP upsell visibility override"
  // in productSelectionRules.js (filterGroupsByAccountContext) for what this
  // gates and how to revert it. Market-split per country, mirroring
  // MC_AU/MC_NZ/MC_Free_Trial above — AU/NZ and US are enabled independently
  // so US can stay off while AU/NZ are on (per the epic's rollout order).
  const isEnabledForAUNZTgpUpgrade = Boolean(
    userFeatures?.Enable_AU_NZ_TGP_Upgrade,
  )
  const isEnabledForUSTgpUpgrade = Boolean(userFeatures?.Enable_US_TGP_Upgrade)
  const isTgpUpgradeEnabled =
    (['AU', 'NZ'].includes(selectedCountry) && isEnabledForAUNZTgpUpgrade) ||
    (selectedCountry === 'US' && isEnabledForUSTgpUpgrade)
  // GEP-7133 / FULSAS-13599: permission flag for MC Plus/Pro -> TGP Starter
  // downgrades — see "TGP Starter is Foundation-only" in
  // productSelectionRules.js (getDisabledPlanCodes) for what this gates.
  const isTgpDowngradeEnabled = Boolean(userFeatures?.Enable_TGP_Downgrade)
  const hasKeapMaxAccess = Boolean(userFeatures?.KeapMax_Catalog_Provisions)
  const hasKeapHVACAccess = Boolean(userFeatures?.ThryvHVAC_Catalog_Provisions)
  const hasCustomDataFYAccess = Boolean(
    userFeatures?.Custom_Data_For_You_Access,
  )
  const isSocialAdsAndTLWithBCEnabled = Boolean(
    userFeatures?.Enable_Social_Ads_And_TL_With_BC,
  )
  const isUserEnabledForBC5NewSales = Boolean(userFeatures?.Bc5newsales)
  const isNewTLMCAccessible = getIsNewTLMCAccessible(userFeatures)

  const {roles = []} = useUserRoles()
  const isPartnerOpsUser = roles.includes('PartnerOps')
  // Thryv Success Services is hidden only for accounts whose ONLY role is PartnerOps,
  // not any account that merely includes it among other roles.
  const isSuccessServicesRestrictedForPartnerOps =
    roles.length === 1 && roles[0] === 'PartnerOps'

  const allAccountSubs = useMemo(() => {
    const payload = recurlyAccountInfo?.payload ?? recurlyAccountInfo ?? {}
    return [
      ...(payload.parentSubscriptions ?? []),
      ...(payload.childSubscriptions ?? []),
    ]
  }, [recurlyAccountInfo])

  // bc-6-thryv-m2m is a standalone BC fallback — hidden once a Keap Max plan
  // is in the cart (mirrors centers-and-apps.js:1353-1355,1426's
  // selectedCentersAndApps-sourced hasKeapMax, which is cart state, not
  // account-owned subs like allAccountSubs/hasBC5Plan above).
  const hasKeapMaxInCart = useMemo(
    () =>
      (activeItem?.items ?? []).some(item =>
        KEAP_FAMILY_CODES.includes(item.productCode),
      ),
    [activeItem?.items],
  )

  const isThryvStarterInCart = useMemo(
    () =>
      (activeItem?.items ?? []).some(item =>
        THRYV_STARTER_CODES.includes(item.productCode),
      ),
    [activeItem?.items],
  )

  // Mirrors legacy isGrowthPackageAlreadyInCart: checks activeItem.items
  // rather than allAccountSubs alone, because items includes alreadyIncluded
  // (account-owned) entries too — so this covers both "already on account"
  // and "just added in this session" in a single check.
  const isGrowthPackageAlreadyInCart = useMemo(
    () =>
      (activeItem?.items ?? []).some(item =>
        GROWTH_PACKAGES.includes(item.productCode),
      ),
    [activeItem?.items],
  )

  const showGrowthPackageOnboarding =
    isThryvStarterInCart && !isGrowthPackageAlreadyInCart

  // cohort-gated Business/Marketing Center access,
  // each defaulting open once its pilot/restriction flags aren't in use.
  const hasBusinessCenterAccess = useMemo(() => {
    const isBcInAccountSubs = allAccountSubs.some(sub =>
      getSubPlanCode(sub)?.startsWith('bc_'),
    )
    if (userFeatures?.BC_Catalog_Update_Only && !isBcInAccountSubs) {
      return false
    }
    if (
      !userFeatures?.BC_Catalog_Provisions_Pilot &&
      !userFeatures?.BC_Catalog_Restrictions
    ) {
      return true
    }
    if (userFeatures?.BC_Catalog_Restrictions) return false
    if (userFeatures?.BC_Catalog_Provisions_Pilot) return true
    return true
  }, [
    userFeatures?.BC_Catalog_Update_Only,
    userFeatures?.BC_Catalog_Provisions_Pilot,
    userFeatures?.BC_Catalog_Restrictions,
    allAccountSubs,
  ])

  const hasMarketingCenterAccess = useMemo(() => {
    if (
      !userFeatures?.MC_Catalog_Provisions_Pilot &&
      !userFeatures?.MC_Catalog_Restrictions
    ) {
      return true
    }
    if (userFeatures?.MC_Catalog_Restrictions) return false
    if (userFeatures?.MC_Catalog_Provisions_Pilot) return true
    return true
  }, [
    userFeatures?.MC_Catalog_Provisions_Pilot,
    userFeatures?.MC_Catalog_Restrictions,
  ])

  const isSocialContentGenGrandfatheredValue = useMemo(
    () => isSocialContentGenGrandfathered(allAccountSubs),
    [allAccountSubs],
  )

  // Whether the Annual billing toggle should be shown for the Marketing
  // Center group — mirrors the Toggle condition in centers-and-apps.js.
  const isNewCustomer = useMemo(
    () =>
      allAccountSubs.every(sub =>
        FREE_ACCOUNT_CODES.includes(getSubPlanCode(sub)),
      ),
    [allAccountSubs],
  )

  const showMCGroupAnnualToggle = useMemo(() => {
    // GEP-7133 / FULSAS-13599: the TGP upgrade flow needs the Marketing
    // Center group's billing toggle rendered (so it can be locked to match
    // the account's existing MC 5.0/TGP billing frequency — see
    // lockedBillingByGroup below) regardless of the US-only free/trial
    // conditions below, which are unrelated to this flow.
    // TO REVERT: delete this line.
    if (isTgpUpgradeEnabled) return true
    if (!isUserEnabledForMCFreeTrial) return false
    if (selectedCountry !== 'US') return false
    if (isPartnerOpsUser) return false
    const hasMCTrialPlan = allAccountSubs.some(sub =>
      MC_TRIAL_PLAN_CODES.includes(getSubPlanCode(sub)),
    )
    const isFreeAccount = allAccountSubs.every(sub =>
      FREE_ACCOUNT_CODES.includes(getSubPlanCode(sub)),
    )
    return hasMCTrialPlan || isFreeAccount
  }, [
    isTgpUpgradeEnabled,
    isUserEnabledForMCFreeTrial,
    selectedCountry,
    isPartnerOpsUser,
    allAccountSubs,
  ])

  const accountItems = useMemo(() => rawAccountItems ?? [], [rawAccountItems])

  // FULSAS-13789: TGP Foundation / Thryv Basic catalog visibility is flag-gated.
  const isTgpFoundationCatalogEnabled =
    isTgpFoundationCatalogFlagOn(userFeatures)
  const isThryvBasicCatalogEnabled = isThryvBasicCatalogFlagOn(userFeatures)

  // accountContext shared by both group filters below — Bc5newsales and the
  // PartnerOps rules apply identically to software (Business/Marketing
  // Center tiers) and addons (Thryv Success Services) groups.
  const groupFilterAccountContext = useMemo(
    () => ({
      selectedCountry,
      allAccountSubs,
      isUserEnabledForMCFreeTrial,
      isUserEnabledForMCAU,
      isUserEnabledForMCNZ,
      isTgpUpgradeEnabled,
      isUserEnabledForBC5NewSales,
      isPartnerOpsUser,
      isSuccessServicesRestrictedForPartnerOps,
      hasKeapMaxInCart,
      accountItems,
      isTgpFoundationCatalogEnabled,
      isThryvBasicCatalogEnabled,
      cartItems: activeItem?.items ?? [],
    }),
    [
      selectedCountry,
      allAccountSubs,
      isUserEnabledForMCFreeTrial,
      isUserEnabledForMCAU,
      isUserEnabledForMCNZ,
      isTgpUpgradeEnabled,
      isUserEnabledForBC5NewSales,
      isPartnerOpsUser,
      isSuccessServicesRestrictedForPartnerOps,
      hasKeapMaxInCart,
      accountItems,
      isTgpFoundationCatalogEnabled,
      isThryvBasicCatalogEnabled,
      activeItem?.items,
    ],
  )

  // Country-restricted plans (e.g. Leads Lift, AU-only, or a needFlag-gated
  // price entry) are filtered out here, before anything else (allPlans,
  // disabledPlanCodes, etc.) derives from these groups — so every downstream
  // consumer, including CatalogGrid, just never sees them for a
  // non-matching country/cohort.
  const softwareGroups = useMemo(() => {
    const byCountry = filterGroupsByCountry(
      rawSoftwareGroups,
      selectedCountry,
      userFeatures,
      {
        ignoreNeedFlag: isKeapPartnerCatalog,
        allAccountSubs,
        isTgpUpgradeEnabled,
      },
    )
    return isKeapPartnerCatalog
      ? byCountry
      : filterGroupsByAccountContext(byCountry, groupFilterAccountContext)
  }, [
    rawSoftwareGroups,
    selectedCountry,
    userFeatures,
    groupFilterAccountContext,
    isKeapPartnerCatalog,
    allAccountSubs,
    isTgpUpgradeEnabled,
  ])

  // Compute which plan code to hide from the UI for each base↔alternate pair.
  // For upgrades where the account already owns the base plan, show the base
  // and hide the alternate (with website). For new orders, show the alternate
  // (with website included) and hide the base.
  // Applied at the display level (CatalogGrid prop) so softwareGroups/allPlans
  // stays unfiltered and getProductConfig can find both sides of the pair.
  const alternatePlanCodesToHide = useMemo(() => {
    const accountCodes = new Set(allAccountSubs.map(getSubPlanCode))
    const isUpgrade = allAccountSubs.some(
      sub => !FREE_ACCOUNT_CODES.includes(getSubPlanCode(sub)),
    )
    const baseToAlternateMap = new Map()
    softwareGroups.forEach(group => {
      group.plans?.forEach(plan => {
        plan.alternatePlanCodes?.forEach(entry => {
          if (
            entry.swap_when_selected &&
            entry.alternate_plan_code !== plan.code
          ) {
            baseToAlternateMap.set(plan.code, entry.alternate_plan_code)
          }
        })
      })
    })
    const planCodesToHide = new Set()
    baseToAlternateMap.forEach((alternateCode, baseCode) => {
      if (isUpgrade && accountCodes.has(baseCode)) {
        planCodesToHide.add(alternateCode)
      } else {
        planCodesToHide.add(baseCode)
      }
    })
    return planCodesToHide
  }, [softwareGroups, allAccountSubs])
  // HIPAA / Restricted Access (Thryv Add-Ons) go through the same
  // account-context filter as the Marketing Center group above — it's a
  // no-op for them today since nothing here sources an industryTemplate yet
  // (see the accountContext destructuring in filterGroupsByAccountContext).
  const addonGroups = useMemo(() => {
    const byCountry = filterGroupsByCountry(
      rawAddonGroups,
      selectedCountry,
      userFeatures,
      {ignoreNeedFlag: isKeapPartnerCatalog},
    )
    const byContext = isKeapPartnerCatalog
      ? byCountry
      : filterGroupsByAccountContext(byCountry, groupFilterAccountContext)
    // Mirror upgrade-order.js:285-303: exactly one TL SKU is offered at a
    // time. When TL_Tax_Plan_Enabled is off, hide the tax variant. When on,
    // hide the non-tax variant — unless the account already owns it, in which
    // case keep the non-tax variant and hide the tax one (no cross-upgrade).
    const isNewTLPlanEnabled = Boolean(userFeatures?.TL_Tax_Plan_Enabled)
    const existingTLWithoutTax = allAccountSubs.some(
      sub => (sub.plan?.code ?? sub.code) === 'tl-mc-5-thryv-m2m',
    )
    const tlCodeToHide = isNewTLPlanEnabled
      ? existingTLWithoutTax
        ? 'tl-tax-5-thryv-m2m'
        : 'tl-mc-5-thryv-m2m'
      : 'tl-tax-5-thryv-m2m'
    return byContext
      .map(g => ({
        ...g,
        plans: g.plans.filter(p => p.code !== tlCodeToHide),
      }))
      .filter(g => g.plans.length > 0)
  }, [
    rawAddonGroups,
    selectedCountry,
    userFeatures,
    groupFilterAccountContext,
    allAccountSubs,
    isKeapPartnerCatalog,
  ])

  const allPlans = useMemo(
    () => [
      ...softwareGroups.flatMap(g => g.plans ?? []),
      ...addonGroups.flatMap(g => g.plans ?? []),
    ],
    [softwareGroups, addonGroups],
  )

  // Includes plans that are filtered from display (e.g. base plans hidden when
  // the alternate/plus plan is shown). Used for alternate-plan price lookups.
  const allPlansUnfiltered = useMemo(
    () => [
      ...rawSoftwareGroups.flatMap(g => g.plans ?? []),
      ...rawAddonGroups.flatMap(g => g.plans ?? []),
    ],
    [rawSoftwareGroups, rawAddonGroups],
  )

  // Builds a lookup from productCode to the plan's catalog config object,
  // used to retrieve business-logic fields (autoAddCenters, disableApps, etc.)
  // without storing them in Redux state.
  const getProductConfig = useMemo(() => {
    const map = Object.fromEntries(allPlans.map(p => [p.code, p]))
    return code => map[code] ?? null
  }, [allPlans])

  const manualCoupons = useSelector(selectManualCoupons)

  // Full flat catalog lookup including hidden/non-UI products (e.g. vertical
  // clientsvcs variants that are auto-injected by HvacAddonPanel but never
  // rendered as selectable cards). getProductConfig only covers UI-visible
  // plans (from softwareGroups/addonGroups), so it misses these.
  const getAutoAddItemConfig = useMemo(() => {
    const allItems = [
      ...(productConfigInfo.centers ?? []),
      ...(productConfigInfo.apps ?? []),
      ...(productConfigInfo.oneTimeFees ?? []),
    ]
    const map = Object.fromEntries(allItems.map(p => [p.code, p]))
    return code => map[code] ?? null
  }, [productConfigInfo])

  const lockedBillingByGroup = useMemo(
    () =>
      getLockedBillingByGroup(accountItems, [
        ...softwareGroups,
        ...addonGroups,
      ]),
    [accountItems, softwareGroups, addonGroups],
  )

  // Only one-time fees with catalog `appliesTo` rules (e.g. the Website
  // Design Fee, Onboarding Fee) are "managed" — auto-attached/detached by
  // this reconciliation based on what else is in the cart. One-time fees
  // with no appliesTo (e.g. Professional Designed Logo, 5 Extra Website
  // Pages) are directly user-selected products, just like a Center or App —
  // they must pass through untouched or they'd vanish the instant they're
  // added (stripped here, never rebuilt since resolveApplicableFees only
  // re-adds fees it can match via appliesTo).
  const managedFeeCodes = useMemo(
    () =>
      new Set(
        oneTimeFeeDefinitions
          .filter(fee => fee.appliesTo?.length)
          .map(fee => fee.code),
      ),
    [oneTimeFeeDefinitions],
  )

  const reconcileFees = useCallback(
    items => {
      // Uses the outer managedFeeCodes (appliesTo-based) — don't redeclare a narrower one here, it'll shadow this and silently strip non-appliesTo fees like Logo/5 Extra Website Pages back out.
      // Flatten nested Center entitlements (e.g. ent-5-website, checked on
      // MC Pro) in alongside top-level codes — otherwise a fee whose
      // appliesTo only lists the entitlement code (not the Center itself)
      // never matches and gets stripped right back out below.
      const flattenCodes = list =>
        list
          .filter(item => item.productType !== 'ONE_TIME_FEE')
          .flatMap(item => [
            item.productCode,
            ...(item.addons?.map(addon => addon.productCode) ?? []),
          ])

      const existingCodes = flattenCodes(accountItems)
      const cartCodes = flattenCodes(items)
      const onboardingDeclined = items.some(item => {
        if (item.productType === 'ONE_TIME_FEE') return false
        const config = item.configuration ?? item.configuredOptions ?? {}
        return config.onboarding === 'no'
      })

      // FULSAS-12295: When a Growth Package is in the cart with
      // includeOnboarding === 'Yes', the package bundles onboarding —
      // waive the separate thryvprfee. Mirrors options.js:2196-2204.
      // Keap guard: if Keap is also in the cart, thryvprfee is a fresh
      // Keap-specific charge and must not be removed.
      const isKeapInCart = items.some(
        item =>
          item.productType !== 'ONE_TIME_FEE' &&
          KEAP_FAMILY_CODES.includes(item.productCode),
      )
      const growthPackageItem = items.find(
        item =>
          item.productType !== 'ONE_TIME_FEE' &&
          GROWTH_PACKAGES.includes(item.productCode),
      )
      const gpConfig =
        growthPackageItem?.configuration ??
        growthPackageItem?.configuredOptions ??
        {}
      const isOnboardingWaivedByGrowthPackage =
        gpConfig.includeOnboarding?.value === 'Yes' && !isKeapInCart

      // WebsiteSettingsModal stamps {isDomainValid: true} onto the website
      // item's own configuration when the customer has an existing,
      // validated site to transfer — appliesTo has no way to express that
      // waiver itself, so it's checked here instead.
      const isWebsiteDesignFeeWaived = items.some(item => {
        if (item.productCode === WEBSITE_ADDON_CODE) {
          return Boolean(item.configuration?.isDomainValid)
        }
        return item.addons?.some(
          addon =>
            addon.productCode === WEBSITE_ENTITLEMENT_CODE &&
            addon.configuration?.isDomainValid,
        )
      })

      // MC 2.0 Signature/Premier plans bundle ent-5-website as a free
      // entitlement — the design fee must not be charged. Mirrors
      // options.js:2133-2145's isMC20PlanSelected condition.
      const isMC20SigPremierInCart = items.some(item =>
        MC_2_0_SIGPREMIERPLANS.includes(item.productCode),
      )

      const feeCodesNeeded = resolveApplicableFees(
        existingCodes,
        cartCodes,
        oneTimeFeeDefinitions.filter(fee => managedFeeCodes.has(fee.code)),
      )
        .filter(code => !(code === 'thryvprfee' && onboardingDeclined))
        .filter(
          code => !(code === 'thryvprfee' && isOnboardingWaivedByGrowthPackage),
        )
        .filter(
          code =>
            !(
              code === WEBSITE_DESIGN_FEE_PLAN.code &&
              (isWebsiteDesignFeeWaived || isMC20SigPremierInCart)
            ),
        )

      // Strip only managed onboarding fees; preserve all other ONE_TIME_FEE items
      const itemsWithoutManagedFees = items.filter(
        item =>
          item.productType !== 'ONE_TIME_FEE' ||
          !managedFeeCodes.has(item.productCode),
      )

      const currency = COUNTRY_TO_CURRENCY[selectedCountry] ?? 'USD'

      const feeItems = feeCodesNeeded.map(feeCode => {
        const feeDef = oneTimeFeeDefinitions.find(f => f.code === feeCode)
        const price = Number(
          feeDef?.currencies?.find(c => c.currency === currency)?.unitAmount ??
            0,
        )
        return {
          productCode: feeCode,
          productName: feeDef?.name ?? 'Onboarding Fee',
          productType: 'ONE_TIME_FEE',
          quantity: 1,
          unitPrice: price,
          dueTodayAmount: price,
          billingFrequency: null,
          configuration: null,
          alreadyIncluded: false,
          addons: [],
        }
      })

      return [...itemsWithoutManagedFees, ...feeItems]
    },
    [accountItems, oneTimeFeeDefinitions, managedFeeCodes, selectedCountry],
  )

  const selectedProducts = (activeItem?.items ?? []).map(item => ({
    planCode: item.productCode,
    quantity: item.quantity,
    // The actual stored line-item price — takes precedence over the catalog
    // base price on the grid card for products priced at add-time rather
    // than from a fixed catalog amount (e.g. Leads Lift's selected tier).
    price: item.unitPrice,
    // Additive fields so ProductPlanSelector can pre-fill / edit a plan
    // that's already in the order (e.g. AEO & SEO's SeoAddonPanel).
    configuredOptions: item.configuration,
    addons: item.addons,
    entitlements: item.entitlements,
    alreadyIncluded: item.alreadyIncluded,
  }))

  // Flat map of planCode → selectedProduct entry, including addons nested
  // inside wrapper items (e.g. Keap sub-addons stored in parent's addons
  // array). Computed once here so every consumer gets O(1) lookup without
  // rebuilding the map per render.
  const selectedProductsByCode = useMemo(
    () => Object.fromEntries(selectedProducts.map(p => [p.planCode, p])),
    [selectedProducts],
  )

  const {disabledPlanCodes} = getRenderRules(
    activeItem?.items ?? [],
    getProductConfig,
    allPlans,
    {
      selectedCountry,
      allAccountSubs,
      clientType,
      isUserEnabledForMCFreeTrial,
      isUserEnabledForMCAU,
      isUserEnabledForMCNZ,
      hasBusinessCenterAccess,
      hasMarketingCenterAccess,
      hasKeapMaxAccess: isKeapPartnerCatalog || hasKeapMaxAccess,
      hasKeapHVACAccess: isKeapPartnerCatalog || hasKeapHVACAccess,
      hasCustomDataFYAccess: isKeapPartnerCatalog || hasCustomDataFYAccess,
      isNewTLMCAccessible,
      isSocialAdsAndTLWithBCEnabled,
      isTgpUpgradeEnabled,
      isTgpDowngradeEnabled,
    },
  )

  // Website downgrade modal: shown when adding an MC 2.0 Starter / MCF causes
  // an existing Signature or Premier plan's ent-5-website to be removed.
  // Holds { resolvedItems, downgradedFromName } until the user answers.
  const [pendingWebsiteDowngradePrompt, setPendingWebsiteDowngradePrompt] =
    useState(null)

  const handleAddProduct = useCallback(
    (product, groupPlanCodes = [], groupId) => {
      if (!activeItem) return

      const newItem = {
        ...product,
        productCode: product.productId,
        groupPlanCodes,
      }

      const alreadyAdded = newItem.productCode in selectedProductsByCode

      if (alreadyAdded) return

      // FULSAS-12280 / BUSREQ-14036/14038: downgrade access is Admin-only at
      // launch, driven by the MC_Downgrade_Access feature flag (same
      // mechanism as MC_AU/MC_NZ) so it's configurable without a code
      // change. Mirrors resolveItemsAfterAdd's own isDowngrade check
      // (productSelectionRules.js) since that function doesn't surface the
      // result - options.js's legacy handleSelectOption has the equivalent
      // gate. order-service.js#assertDowngradeAccessAllowed re-checks
      // server-side regardless of which catalog UI initiated the order.
      // TGP-DOWNGRADE #4 (see docs/changes/TGP-DOWNGRADE-grayout-and-gating.md
      // for the original isSameGroupDowngrade/isCrossGroupDowngrade code to
      // restore). The two *DowngradedFrom lists below are those same checks,
      // kept as lists instead of booleans so the TGP exemption can inspect
      // which plans are being downgraded.
      const sameGroupDowngradedFrom =
        groupPlanCodes.length > 0
          ? activeItem.items.filter(
              item =>
                groupPlanCodes.includes(item.productCode) &&
                item.productCode !== newItem.productCode &&
                (item.productRank ?? 0) > (newItem.productRank ?? 0),
            )
          : []

      // Cross-group downgrade: e.g. MC plan → Starter (different groups).
      // Only fires when both sides have an explicit productRank, so add-ons
      // and unranked products are never mistakenly treated as downgrades.
      const crossGroupDowngradedFrom =
        newItem.productRank != null
          ? activeItem.items.filter(
              item =>
                !groupPlanCodes.includes(item.productCode) &&
                item.productCode !== newItem.productCode &&
                item.productRank != null &&
                item.productRank > newItem.productRank,
            )
          : []

      const isCenterDowngrade =
        sameGroupDowngradedFrom.length > 0 ||
        crossGroupDowngradedFrom.length > 0

      // TGP Starter / Signature → TGP Foundation / Thryv Basic is a TGP
      // downgrade: allowed with Enable_TGP_Downgrade (or the broader
      // MC_Downgrade_Access). Every other downgrade keeps the checks below.
      const isTgpDowngradeToFoundation =
        isCenterDowngrade &&
        isTgpFoundationDowngrade(
          newItem.productCode,
          [...sameGroupDowngradedFrom, ...crossGroupDowngradedFrom].map(
            item => item.productCode,
          ),
        )

      if (isTgpDowngradeToFoundation) {
        if (
          !userFeatures.Enable_TGP_Downgrade &&
          !userFeatures.MC_Downgrade_Access
        ) {
          toast.error('Your role is not authorized to downgrade this plan.')
          return
        }
      } else if (
        !newItem?.productCode.includes('mc_starter') &&
        isCenterDowngrade &&
        !userFeatures.MC_Downgrade_Access
      ) {
        toast.error('Your role is not authorized to downgrade this plan.')
        return
      } else if (
        newItem?.productCode.includes('mc_starter') &&
        isCenterDowngrade &&
        !userFeatures.Enable_TGP_Downgrade
      ) {
        toast.error('Your role is not authorized to downgrade this plan.')
        return
      }

      const items = resolveItemsAfterAdd(activeItem.items, newItem, {
        groupPlanCodes,
        groupId,
        selectedCountry,
        getProductConfig,
        getAutoAddItemConfig,
      })

      // Website downgrade check: if a Sig/Premier's ent-5-website was removed
      // by this add (Starter or MCF replacing it), ask whether to retain the
      // website as a paid standalone add-on. Mirrors options.js:2255-2294.
      // Check the current cart (activeItem.items), not accountItems, so the
      // modal only fires when Signature/Premier is actively in the cart and
      // being replaced — not on every subsequent add after the downgrade has
      // already happened (accountItems still reflects the original account
      // subscriptions and would retrigger the modal for unrelated adds).
      const hadWebsiteEnt = activeItem.items.some(
        item =>
          MC_2_0_SIGPREMIERPLANS.includes(item.productCode) &&
          item.addons?.some(
            addon => addon.productCode === WEBSITE_ENTITLEMENT_CODE,
          ),
      )
      const nowHasWebsiteEnt = items.some(item =>
        item.addons?.some(
          addon => addon.productCode === WEBSITE_ENTITLEMENT_CODE,
        ),
      )
      if (hadWebsiteEnt && !nowHasWebsiteEnt) {
        const prevPlanWithWebsite = accountItems.find(
          item =>
            MC_2_0_SIGPREMIERPLANS.includes(item.productCode) &&
            item.addons?.some(
              addon => addon.productCode === WEBSITE_ENTITLEMENT_CODE,
            ),
        )
        setPendingWebsiteDowngradePrompt({
          resolvedItems: items,
          downgradedFromName: prevPlanWithWebsite?.name ?? 'Thryv Signature',
        })
        return
      }

      onItemsChange(reconcileFees(items))
    },
    [
      activeItem,
      accountItems,
      selectedCountry,
      getProductConfig,
      getAutoAddItemConfig,
      reconcileFees,
      selectedProductsByCode,
      userFeatures.MC_Downgrade_Access,
      userFeatures.Enable_TGP_Downgrade,
      onItemsChange,
    ],
  )

  // Website downgrade modal response:
  //   answer === null  → "Back" — cancel the add entirely
  //   answer === 'no'  → proceed with the downgrade, website removed
  //   answer === 'yes' → proceed and add addon-5-website_difm as a paid add-on
  //                      (no design fee since the website already exists —
  //                      reconcileFees skips thryvdesg2 when isDomainValid is set,
  //                      but here the add-on carries no configuration, so the
  //                      design fee waiver is handled by the existing
  //                      isWebsiteDesignFeeWaived check in reconcileFees which
  //                      reads isDomainValid off the item's configuration).
  //
  // The design fee is intentionally NOT charged (mirrors options.js:2277-2291):
  // the website already exists, only the billing relationship changes.
  const handleWebsiteDowngradeAnswer = useCallback(
    answer => {
      if (!pendingWebsiteDowngradePrompt || !activeItem) return

      if (answer === null) {
        // "Back" — abort, leave cart unchanged
        setPendingWebsiteDowngradePrompt(null)
        return
      }

      let {resolvedItems} = pendingWebsiteDowngradePrompt

      if (answer === 'yes') {
        const websitePlan = getProductConfig(WEBSITE_ADDON_CODE)
        const alreadyInCart = resolvedItems.some(
          item => item.productCode === WEBSITE_ADDON_CODE,
        )
        if (websitePlan && !alreadyInCart) {
          const websiteItem = {
            productId: WEBSITE_ADDON_CODE,
            name: websitePlan.name,
            price: getPriceForCountry(websitePlan.currencies, selectedCountry),
            productType: 'APP',
            billingFrequency:
              websitePlan.productBillingType === 'Annual'
                ? 'annual'
                : 'monthly',
            quantity: 1,
            configuredOptions: {isDomainValid: true},
            excludes: websitePlan.excludes ?? [],
            maxQty: websitePlan.maxQty ?? null,
            parentProduct: websitePlan.parentProduct,
          }
          resolvedItems = resolveItemsAfterAdd(resolvedItems, websiteItem, {
            groupPlanCodes: [],
            groupId: websitePlan.uigroup,
            selectedCountry,
            getProductConfig,
            getAutoAddItemConfig,
          })
        }
      }

      onItemsChange(reconcileFees(resolvedItems))
      setPendingWebsiteDowngradePrompt(null)
    },
    [
      activeItem,
      pendingWebsiteDowngradePrompt,
      selectedCountry,
      getProductConfig,
      getAutoAddItemConfig,
      reconcileFees,
      onItemsChange,
    ],
  )

  // Updates a plan already in the order (e.g. AEO and SEO PowerBoost/Keywords).
  // Same-code updates are applied in place.
  // Code-changing updates still use the normal group replacement rules.
  const handleUpdateProduct = useCallback(
    (product, updateContext = {}) => {
      if (!activeItem) return

      const updatedItem = {
        ...product,
        productCode: product.productId,
      }

      const isPlanCodeChange =
        updateContext.previousProductCode &&
        updateContext.previousProductCode !== updatedItem.productCode
      const items = isPlanCodeChange
        ? resolveItemsAfterAdd(activeItem.items, updatedItem, {
            groupPlanCodes: updateContext.groupPlanCodes ?? [],
            groupId: updateContext.groupId,
            selectedCountry,
            getProductConfig,
            getAutoAddItemConfig,
          })
        : resolveItemsAfterUpdate(activeItem.items, updatedItem)

      onItemsChange(reconcileFees(items))
    },
    [
      activeItem,
      selectedCountry,
      getProductConfig,
      getAutoAddItemConfig,
      reconcileFees,
      onItemsChange,
    ],
  )

  // Dedicated flow for quantity-driven items (stepper addons): each +/-
  // click re-targets the item's productCode and updates its quantity in
  // place, bypassing the group replace/coexist rules in handleAddProduct.
  const handleQuantityChange = useCallback(
    (product, quantity) => {
      if (!activeItem) return

      const newItem = {
        ...product,
        productCode: product.productId,
        quantity,
      }

      const items = applyQuantityToItems(activeItem.items, newItem)

      onItemsChange(items)
    },
    [activeItem, onItemsChange],
  )

  const handleRemoveProduct = useCallback(
    productCode => {
      if (!activeItem) return
      const items = resolveItemsAfterRemove(
        activeItem.items,
        productCode,
        getProductConfig,
        {accountItems, selectedCountry, getAutoAddItemConfig},
      )
      onItemsChange(reconcileFees(items))
      if (manualCoupons.length > 0) {
        toast.warning(
          'Oops! Your cart changed and some coupons bailed. Re-add them to reel those savings back in',
        )
        dispatch(setManualCoupons([]))
      }
    },
    [
      activeItem,
      getProductConfig,
      manualCoupons,
      accountItems,
      reconcileFees,
      selectedCountry,
      getAutoAddItemConfig,
      onItemsChange,
      dispatch,
    ],
  )

  return {
    softwareGroups,
    addonGroups,
    alternatePlanCodesToHide,
    allPlansUnfiltered,
    isDataLoaded,
    selectedCountry,
    selectedProducts,
    selectedProductsByCode,
    disabledPlanCodes,
    validExtensionList,
    lockedBillingByGroup,
    showMCGroupAnnualToggle,
    isNewTLMCAccessible,
    isNewCustomer,
    isSocialContentGenGrandfathered: isSocialContentGenGrandfatheredValue,
    showGrowthPackageOnboarding,
    handleAddProduct,
    handleUpdateProduct,
    handleQuantityChange,
    handleRemoveProduct,
    pendingWebsiteDowngradePrompt,
    handleWebsiteDowngradeAnswer,
  }
}
